import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ControlPanel, Params } from "./components/ControlPanel";
import pluginConfig from "./sales-plugin.json";
import { centroids, clusterInfo } from "./clusterConfig";
import { DomPredictionPanel } from "./components/DomPredictionPanel";
import { MarketAbsorptionChart } from "./components/MarketAbsorptionChart";
import { MarketForecastPanel } from "./components/MarketForecastPanel";
import { AmenityOutput } from "./components/AmenityOutput";
import { client } from "@sigmacomputing/plugin";
import { useSigmaElementData } from "./lib/useSigmaElementData";
import type { CloneEntry } from "./components/CloneNeighborhoodControl";
import { useSystemTheme } from "./hooks/useSystemTheme";
import "./styles/theme.css";

// 🔗 Import the CSV as a build-time URL
import rateCsvUrl from "../../server/models/rate_history.csv";

client.config.configureEditorPanel([
  { name: "ehMaster", type: "element", label: "EH MASTER" },

  { name: "addressCol",           type: "column", source: "ehMaster", label: "Address",                 allowMultiple: false },
  { name: "cityCol",              type: "column", source: "ehMaster", label: "City",                    allowMultiple: false },
  { name: "neighborhoodCol",      type: "column", source: "ehMaster", label: "Neighborhood - Grouped",  allowMultiple: false },
  { name: "houseTypeCol",         type: "column", source: "ehMaster", label: "House Type",              allowMultiple: false },
  { name: "contractDateCol",      type: "column", source: "ehMaster", label: "Contract Date",           allowMultiple: false },
  { name: "officeGreenLightCol",  type: "column", source: "ehMaster", label: "Office Green Light",      allowMultiple: false },
  { name: "activeCompsCol",       type: "column", source: "ehMaster", label: "Active Comps",            allowMultiple: false },
  { name: "marketCol",            type: "column", source: "ehMaster", label: "Market",                  allowMultiple: false },
  { name: "schoolDistrictCol",    type: "column", source: "ehMaster", label: "School District",         allowMultiple: false },
  { name: "floorPlanCol",         type: "column", source: "ehMaster", label: "Floor Plan",              allowMultiple: false },
  { name: "styleCol",             type: "column", source: "ehMaster", label: "Style",                   allowMultiple: false },
  { name: "priceCol",             type: "column", source: "ehMaster", label: "Price",                   allowMultiple: false },
  { name: "sqftCol",              type: "column", source: "ehMaster", label: "Square Feet",             allowMultiple: false }
]);

const DEFAULT_PARAMS: Params = {
  interestRate: pluginConfig.defaults.interestRate,
  interestRateThreeMonthChange: pluginConfig.defaults.interestRateThreeMonthChange,
  interestRateSixMonthChange: pluginConfig.defaults.interestRateSixMonthChange,
  interestRateNineMonthChange: pluginConfig.defaults.interestRateNineMonthChange,
  interestRateTwelveMonthChange: pluginConfig.defaults.interestRateTwelveMonthChange,
  interestRateTwoYearChange: pluginConfig.defaults.interestRateTwoYearChange,
  initialSpecs: pluginConfig.defaults.initialSpecs,
  specsToStart: pluginConfig.defaults.specsToStart,
  perSpecsSold: pluginConfig.defaults.perSpecsSold,
  preSolds: pluginConfig.defaults.preSolds,
  avgPriceMin: pluginConfig.defaults.avgPriceMin,
  avgPriceMax: pluginConfig.defaults.avgPriceMax,
  availableLots: pluginConfig.defaults.availableLots,
  squareFootMin: pluginConfig.defaults.squareFootMin,
  squareFootMax: pluginConfig.defaults.squareFootMax,
  compositionPct: pluginConfig.defaults.composition * 100,
  amenities: [...pluginConfig.defaults.amenities],
  threeMonthPriceChange: pluginConfig.defaults.threeMonthPriceChange,
  sixMonthPriceChange: pluginConfig.defaults.sixMonthPriceChange,
  nineMonthPriceChange: pluginConfig.defaults.nineMonthPriceChange,
  twelveMonthPriceChange: pluginConfig.defaults.twelveMonthPriceChange,
  twoYearPriceChange: pluginConfig.defaults.twoYearPriceChange,
  bedrooms: pluginConfig.defaults.bedrooms,
  baths: pluginConfig.defaults.baths,
  stories: pluginConfig.defaults.stories,
  yearBuilt: pluginConfig.defaults.yearBuilt,
  garageType: pluginConfig.defaults.garageType,
  garageSpaces: pluginConfig.defaults.garageSpaces,
  city: pluginConfig.defaults.city,
  lotType: pluginConfig.defaults.lotType,
  listingDay: pluginConfig.defaults.listingDay,
  listingMonth: pluginConfig.defaults.listingMonth,
  activeComps: pluginConfig.defaults.activeComps,
  neighborhoodID: pluginConfig.defaults.id,
  neighborhoodTitle: pluginConfig.defaults.title,
  cloneType: pluginConfig.defaults.cloneType,
  cloneDate: new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" }),
};

// --- robust CSV parser ---
function parseLatestRate(csvText: string): number | undefined {
  const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return;

  const header = lines[0].split(/,\s*/).map(s => s.trim().toLowerCase());
  const dateIdx = header.indexOf("date");
  const rateIdx = header.indexOf("rate");
  if (dateIdx === -1 || rateIdx === -1) return;

  const rows = lines.slice(1).map(line => {
    const cols = line.split(/,\s*/);
    const rawDate = (cols[dateIdx] || "").trim();
    const rawRate = (cols[rateIdx] || "").trim().replace(/%$/, "");
    const m = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const dateMs = m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : Date.parse(rawDate);
    const num = Number(rawRate);
    const ratePct = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : NaN;
    return { dateMs, ratePct };
  });

  const valid = rows.filter(r => Number.isFinite(r.dateMs) && Number.isFinite(r.ratePct));
    if (!valid.length) return;

    valid.sort((a, b) => b.dateMs - a.dateMs);
    return valid[0].ratePct;
  }

  // --- Get rate closest to a given date ---
  function parseRateForDate(csvText: string, targetDate: string): number | undefined {
    const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;

    const header = lines[0].split(/,\s*/).map(s => s.trim().toLowerCase());
    const dateIdx = header.indexOf("date");
    const rateIdx = header.indexOf("rate");
    if (dateIdx === -1 || rateIdx === -1) return;

    const targetMs = new Date(targetDate).getTime();
    if (isNaN(targetMs)) return;

    const rows = lines.slice(1).map(line => {
      const cols = line.split(/,\s*/);
      const d = Date.parse(cols[dateIdx]);
      const r = parseFloat((cols[rateIdx] || "").replace("%", ""));
      return { dateMs: d, ratePct: Number.isFinite(r) ? (r <= 1 ? r * 100 : r) : NaN };
    }).filter(r => Number.isFinite(r.dateMs) && Number.isFinite(r.ratePct));

    if (!rows.length) return;

    rows.sort((a, b) => Math.abs(a.dateMs - targetMs) - Math.abs(b.dateMs - targetMs));
    return rows[0].ratePct;
  }

  async function getRateForDate(targetDate: string): Promise<number | undefined> {
    try {
      // Disable caching to force fresh reads
      const res = await fetch(rateCsvUrl + `?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
  
      const text = await res.text();
      const rate = parseRateForDate(text, targetDate);
  
      if (typeof rate === "number" && Number.isFinite(rate)) {
        return Number(rate.toFixed(3));
      }
    } catch (err) {
      console.error("Error fetching rate for date:", err);
    }
    return undefined;
  }
const RATES_URL = rateCsvUrl;

// helper: one-time fetch
async function getLatestRate(): Promise<number | undefined> {
  try {
    const res = await fetch(RATES_URL, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    const latest = parseLatestRate(text);
    if (typeof latest === "number" && Number.isFinite(latest)) {
      return Number(latest.toFixed(3));
    }
  } catch {
    // swallow error
  }
  return undefined;
}

type ForecastRecord = {
  date: string;
  presolds: number;
  sold_specs: number;
  unsold_specs: number;
  available_lots: number;
};

export type ServerComposition = {
  spec_pct?: number | string | null;
  presold_pct?: number | string | null;
  specPct?: number | string | null;
  presoldPct?: number | string | null;
  comp_fraction?: number | string | null;
  compFraction?: number | string | null;
} | null;

export function SalesForecast() {
  const [params, setParams] = useState<Params>({ ...DEFAULT_PARAMS });
  const [triggerDom, setTriggerDom] = useState(false);
  const [triggerForecast, setTriggerForecast] = useState(false);
  const [forecastData, setForecastData] = useState<ForecastRecord[]>([]);
  const [domPredictions, setDomPredictions] =
    useState<{ [k: string]: number | null } | null>(null);
  const [selectedClone, setSelectedClone] = useState<CloneEntry | null>(null);
  const [composition, setComposition] = useState<ServerComposition>(null);
  const theme = useSystemTheme();
  const elementRows = useSigmaElementData();

  // Parse "YYYY-MM-DD" as a LOCAL date at midnight
  function parseLocalYMD(ymd: string): Date {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1); // local time
  }

  // 🔹 On mount: set interestRate from rate_history.csv ONCE
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const latest = await getLatestRate();
      if (!cancelled && typeof latest === "number") {
        setParams(p => ({ ...p, interestRate: latest }));
        setParams(p => ({ ...p, interestRateThreeMonthChange: latest }));
        setParams(p => ({ ...p, interestRateSixMonthChange: latest }));
        setParams(p => ({ ...p, interestRateNineMonthChange: latest }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 🔹 When cloneDate changes, recompute interest rates for each offset (3mo, 6mo, etc.)
  useEffect(() => {
    if (!params.cloneDate) return;

    console.log("📆 cloneDate changed → recomputing interest rates for", params.cloneDate);

    (async () => {
      const baseDate = parseLocalYMD(params.cloneDate);
      const today = new Date();
      const addMonths = (d: Date, m: number) => new Date(d.getFullYear(), d.getMonth() + m, d.getDate());

      const offsets = [
        { key: "interestRate", months: 0 },
        { key: "interestRateThreeMonthChange", months: 3 },
        { key: "interestRateSixMonthChange", months: 6 },
        { key: "interestRateNineMonthChange", months: 9 },
        { key: "interestRateTwelveMonthChange", months: 12 },
        { key: "interestRateTwoYearChange", months: 24 },
      ] as const;

      const results: Record<string, number | undefined> = {};

      for (const { key, months } of offsets) {
        const target = addMonths(baseDate, months);
        console.log("📆 interestRate changes → recomputing interest rates for", target);
        if (target > today) {
          // Future date → fallback to default
          results[key] = (pluginConfig.defaults as any)[key];
        } else {
          results[key] = await getRateForDate(target.toISOString().split("T")[0]);
        }
      }

      setParams(prev => {
      const updated = { ...prev };
      for (const [key, value] of Object.entries(results)) {
        const safeDefault = (pluginConfig.defaults as any)[key] ?? prev[key as keyof Params];

        // prefer the new value if valid; otherwise keep previous or default
        (updated as Record<string, any>)[key] =
          typeof value === "number" && isFinite(value) && value > 0
            ? Number(value.toFixed(3))
            : safeDefault;
      }

      console.log("🔹 Updated interest rates:", updated);
      return updated;
    });
    })();
  }, [params.cloneDate]);

  // Reset handler: no refetch, just defaults
  const handleReset = useCallback(() => {
    setParams({ ...DEFAULT_PARAMS });
    setDomPredictions(null);
    setComposition(null);
  }, []);

  const handleForecastClick = useCallback(() => {
    setTriggerDom(true);
    setTriggerForecast(true);
  }, []);

  // --- Stable top-level clone handler ---
  const handleCloneSelected = useCallback((entry: CloneEntry) => {
    console.log("[SalesForecast] Clone selected:", entry.title);

    // Update any shared state if needed
    setSelectedClone(entry);

    // Optionally sync parameters or reset other parts of the UI here
    setComposition(null);
    setDomPredictions(null);
    setForecastData([]);
  }, []);

  function squaredDistance(a: number[], b: number[]): number {
    return a.reduce((sum, val, i) => sum + Math.pow(val - b[i], 2), 0);
  }
  const memoizedParams = useMemo(() => ({ ...params }), [params]);

  const featureVector = useMemo<number[]>(() => {
    const pond = memoizedParams.amenities.includes("Pond");
    const largeLots = memoizedParams.amenities.includes("Large Lots");
    const gated = memoizedParams.amenities.includes("Gated");
    const pool = memoizedParams.amenities.includes("Pool");
    const clubhouse = memoizedParams.amenities.includes("Clubhouse");
    const kidsRec =
      memoizedParams.amenities.includes("Playground") ||
      memoizedParams.amenities.includes("Splash Pad");
    const adultsRec = ["Tennis", "Basketball", "Pickleball", "Walking Trails"].some(
      (a) => memoizedParams.amenities.includes(a)
    );

    return [pond ? 1 : 0, largeLots ? 1 : 0, gated ? 1 : 0, pool ? 1 : 0, clubhouse ? 1 : 0, kidsRec ? 1 : 0, adultsRec ? 1 : 0];
  }, [memoizedParams]);

  const cluster = useMemo<number>(() => {
    let best = 0;
    let bestDist = Infinity;
    centroids.forEach((c, i) => {
      const dist = squaredDistance(featureVector, c);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, [featureVector]);

  const clusterName = clusterInfo[cluster].name;
  const clusterDescription = clusterInfo[cluster].description;

  return (
    <div className={`plugin-root theme-${theme}`} style={{ backgroundColor: "var(--background-color)", color: "var(--text-color)", minHeight: "100vh", padding: "1rem" }}>
      <div className="flex gap-6 items-start">
        <aside className="w-96 shrink-0">
          <ControlPanel
            params={params}
            setParams={setParams}
            resetDefaults={handleReset}
            onForecastClick={handleForecastClick}
            onCloneSelected={(entry) => handleCloneSelected(entry)}
          />
        </aside>

        <main className="max-h-96 flex-1 flex flex-col gap-6">
          <div className="mt-4">
            <MarketForecastPanel
              params={memoizedParams}
              cluster={cluster}
              trigger={triggerForecast}
              setTrigger={setTriggerForecast}
              setForecastData={setForecastData}
              domPredictions={domPredictions}
              elementRows={elementRows}
              selectedClone={selectedClone}
              setComposition={setComposition}
            />
            <MarketAbsorptionChart key={forecastData.length} data={forecastData} cloneDate={params.cloneDate}/>
          </div>
        </main>

        <aside className="w-72 flex flex-col gap-4">
          <DomPredictionPanel
            params={memoizedParams}
            trigger={triggerDom}
            setTrigger={setTriggerDom}
            setDomPredictions={setDomPredictions}
          />
          <AmenityOutput
            onChange={(updatedAmenities) =>
              setParams((prev) => ({ ...prev, amenities: updatedAmenities }))
            }
            clusterName={clusterName}
            clusterDescription={clusterDescription}
            composition={composition}
            forecastData={forecastData}
            params={memoizedParams}
            elementRows={elementRows}
          />
        </aside>
      </div>
    </div>
  );
}
